import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { celebrateBadges } from "../lib/gamification";
import { BookOpenCheck, Loader2, RefreshCw, Lightbulb, Check, X } from "lucide-react";

const FOCUS_OPTIONS = [
  { id: "mixed", label: "Mixed" },
  { id: "subject-verb agreement", label: "Subject-Verb" },
  { id: "comma usage", label: "Commas" },
  { id: "run-on sentences", label: "Run-ons" },
  { id: "sentence fragments", label: "Fragments" },
  { id: "apostrophes and possessives", label: "Apostrophes" },
];

export default function GrammarLab() {
  const [focus, setFocus] = useState("mixed");
  const [drill, setDrill] = useState(null);
  const [attempt, setAttempt] = useState("");
  const [hintShown, setHintShown] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [loadingDrill, setLoadingDrill] = useState(false);
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [stats, setStats] = useState({ attempted: 0, correct: 0 });

  const newDrill = async () => {
    setLoadingDrill(true); setFeedback(null); setAttempt(""); setHintShown(false);
    try {
      const { data } = await api.post("/grammar/drill", { focus });
      setDrill(data);
    } catch { toast.error("Couldn't generate a drill."); }
    finally { setLoadingDrill(false); }
  };

  const submit = async () => {
    if (!attempt.trim()) { toast.error("Type your corrected sentence."); return; }
    setLoadingCheck(true);
    try {
      const { data } = await api.post("/grammar/check", {
        original: drill.sentence_with_error,
        correct: drill.correct_sentence,
        student_attempt: attempt,
        error_type: drill.error_type || "",
      });
      setFeedback(data);
      setStats(s => ({ attempted: s.attempted + 1, correct: s.correct + (data.is_correct ? 1 : 0) }));
      celebrateBadges(data._meta);
    } catch { toast.error("Could not check your answer."); }
    finally { setLoadingCheck(false); }
  };

  const accuracy = stats.attempted ? Math.round((stats.correct / stats.attempted) * 100) : 0;

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center"><BookOpenCheck size={20}/></div>
          <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Grammar Lab</h1>
        </div>
        <p className="text-slate-600">One sentence. One error. One explanation. The grammar drill that actually teaches.</p>
      </div>

      {/* Focus + Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="tactile-card p-5 md:col-span-2">
          <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">Focus</div>
          <div className="flex gap-2 flex-wrap">
            {FOCUS_OPTIONS.map(f => (
              <button key={f.id} data-testid={`grammar-focus-${f.id}`} onClick={()=>setFocus(f.id)}
                className={`rounded-full px-3 py-1.5 text-xs font-bold border-2 ${focus===f.id ? 'bg-emerald-500 border-emerald-600 text-white':'bg-white border-slate-200 text-slate-700 hover:border-emerald-300'}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="tactile-card p-5">
          <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">Session accuracy</div>
          <div className="flex items-baseline gap-2">
            <span data-testid="grammar-accuracy" className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>{accuracy}%</span>
            <span className="text-sm text-slate-500">{stats.correct}/{stats.attempted}</span>
          </div>
        </div>
      </div>

      {!drill && (
        <div className="tactile-card p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mx-auto mb-4">
            <BookOpenCheck size={22}/>
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{fontFamily:'Outfit'}}>Ready to drill?</h2>
          <p className="text-slate-600 mb-6">Generate a sentence with one grammar error. Spot it. Fix it. Learn the rule.</p>
          <button data-testid="grammar-start" onClick={newDrill} disabled={loadingDrill}
            className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold rounded-full px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60"
            style={{boxShadow:'0 4px 0 0 rgb(4 120 87)'}}>
            {loadingDrill ? <><Loader2 className="animate-spin" size={14}/> Loading…</> : "Start drill"}
          </button>
        </div>
      )}

      {drill && (
        <div className="space-y-4">
          <div className="tactile-card p-6 sm:p-8">
            <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">Find and fix the error</div>
            <p data-testid="drill-sentence" className="text-xl text-slate-900 leading-relaxed font-medium" style={{fontFamily:'Outfit'}}>
              "{drill.sentence_with_error}"
            </p>
            {hintShown && drill.hint && (
              <div data-testid="drill-hint" className="mt-4 rounded-2xl bg-amber-50 border-2 border-amber-100 p-4 flex items-start gap-3">
                <Lightbulb size={18} className="text-amber-600 mt-0.5 flex-shrink-0"/>
                <div>
                  <div className="text-xs uppercase font-bold tracking-widest text-amber-800 mb-1">Hint</div>
                  <div className="text-slate-800">{drill.hint}</div>
                </div>
              </div>
            )}
          </div>

          <div className="tactile-card p-6 sm:p-8 space-y-4">
            <div>
              <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Your correction</label>
              <textarea data-testid="drill-attempt" value={attempt} onChange={(e)=>setAttempt(e.target.value)}
                disabled={!!feedback} rows={3} placeholder="Rewrite the sentence correctly…"
                className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-emerald-400 px-4 py-3 outline-none resize-none disabled:opacity-60" />
            </div>
            <div className="flex gap-2 flex-wrap">
              {!feedback && (
                <>
                  <button data-testid="drill-submit" onClick={submit} disabled={loadingCheck}
                    className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold rounded-full px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
                    style={{boxShadow:'0 4px 0 0 rgb(4 120 87)'}}>
                    {loadingCheck ? <><Loader2 className="animate-spin" size={14}/> Checking…</> : "Check answer"}
                  </button>
                  {!hintShown && drill.hint && (
                    <button data-testid="drill-hint-button" onClick={()=>setHintShown(true)}
                      className="bg-white border-2 border-amber-200 text-amber-800 font-bold rounded-full px-5 py-2.5 inline-flex items-center gap-2 hover:border-amber-300">
                      <Lightbulb size={14}/> Show hint
                    </button>
                  )}
                </>
              )}
              {feedback && (
                <button data-testid="drill-next" onClick={newDrill} disabled={loadingDrill}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-5 py-2.5 inline-flex items-center gap-2 tactile-shadow disabled:opacity-60">
                  {loadingDrill ? <Loader2 className="animate-spin" size={14}/> : <RefreshCw size={14}/>} Next drill
                </button>
              )}
            </div>
          </div>

          {feedback && (
            <div data-testid="drill-feedback" className="tactile-card p-6 sm:p-8 fade-up">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full grid place-items-center ${feedback.is_correct ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {feedback.is_correct ? <Check size={20}/> : <X size={20}/>}
                </div>
                <h3 className="text-2xl font-bold" style={{fontFamily:'Outfit'}}>
                  {feedback.is_correct ? "Nailed it!" : "Close — keep going"}
                </h3>
              </div>
              <p className="text-slate-900 mb-4">{feedback.feedback}</p>
              <div className="rounded-2xl bg-indigo-50 border border-indigo-100 p-4 mb-3">
                <div className="text-xs uppercase font-bold tracking-widest text-indigo-800 mb-1">The rule</div>
                <p className="text-slate-800">{feedback.explanation}</p>
              </div>
              {feedback.remember_this && (
                <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4">
                  <div className="text-xs uppercase font-bold tracking-widest text-amber-800 mb-1">Remember this</div>
                  <p className="text-slate-900 font-semibold">{feedback.remember_this}</p>
                </div>
              )}
              {!feedback.is_correct && (
                <div className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
                  <div className="text-xs uppercase font-bold tracking-widest text-emerald-800 mb-1">Correct version</div>
                  <p className="text-slate-900 italic">"{drill.correct_sentence}"</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
